import { mockNotificationSeeds } from '@/mocks/notifications';
import { mockReviewSeeds } from '@/mocks/reviews';
import { getOrderViews } from './ordersService';
import { delay, shouldSimulateError } from './simulation';
import type { NotificationRecord, NotificationView } from '@/types';

/** Build an ISO date that is always in the past from a "minutes ago" seed. */
function fromMinutesAgo(minutesAgo: number): string {
  return new Date(Date.now() - minutesAgo * 60000).toISOString();
}

/**
 * In-memory notification log + a tiny pub/sub so live arrivals can be handled.
 * Demo seed times are relative ("minutes ago") so they are always in the past.
 * This is a clearly replaceable demo layer — no backend is connected, so the
 * "live" delivery below is a preview mechanism only (see simulateIncomingEvent).
 */
let store: NotificationRecord[] = mockNotificationSeeds.map((seed) => ({
  id: seed.id,
  kind: seed.kind,
  createdAt: fromMinutesAgo(seed.createdMinutesAgo),
  orderId: seed.orderId ?? null,
  reviewId: seed.reviewId ?? null,
  customerName: seed.customerName ?? null,
  rating: seed.rating ?? null,
  packageName: seed.packageName ?? null,
}));

/**
 * Ids the laundry has flagged as handled in this demo session. Tracking this
 * separately from the raw record keeps the system record untouched while still
 * surviving navigation and refetches.
 */
const processedIds = new Set<string>();

type Listener = (view: NotificationView) => void;
const listeners = new Set<Listener>();

/** Subscribe to live notification arrivals. Returns an unsubscribe function. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(view: NotificationView): void {
  listeners.forEach((listener) => listener(view));
}

function sortNewest(views: NotificationView[]): NotificationView[] {
  return [...views].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/**
 * Resolve a raw record into a display view. Customer name / rating for reviews
 * are read from the same reviews dataset the reviews page uses; order links are
 * checked against the live orders store so a stale link is never offered.
 */
function buildView(record: NotificationRecord, orderIds: Set<string>): NotificationView {
  const base: NotificationView = {
    id: record.id,
    kind: record.kind,
    createdAt: record.createdAt,
    orderId: record.orderId ?? null,
    customerName: record.customerName ?? null,
    rating: record.rating ?? null,
    packageName: record.packageName ?? null,
    to: null,
    targetMissing: false,
    processed: processedIds.has(record.id),
  };

  switch (record.kind) {
    case 'new_order':
    case 'order_handoff':
    case 'manual_assignment': {
      if (record.orderId && orderIds.has(record.orderId)) {
        base.to = `/dashboard/orders/${record.orderId}`;
      } else {
        base.targetMissing = Boolean(record.orderId);
      }
      return base;
    }
    case 'new_review': {
      const review = record.reviewId
        ? mockReviewSeeds.find((item) => item.id === record.reviewId)
        : undefined;
      if (review) {
        base.orderId = review.orderId;
        base.customerName = review.customerName;
        base.rating = review.rating;
        base.to =
          review.targetId === 'laundry'
            ? '/dashboard/reviews'
            : `/dashboard/reviews/drivers/${review.targetId}`;
      } else {
        base.to = '/dashboard/reviews';
      }
      return base;
    }
    case 'package_subscription':
    default:
      // No approved destination for package subscriptions yet.
      return base;
  }
}

function currentOrderIds(): Set<string> {
  return new Set(getOrderViews().map((order) => order.id));
}

export async function fetchNotifications(): Promise<NotificationView[]> {
  await delay();
  if (shouldSimulateError('notifications')) {
    throw new Error('failed-to-load-notifications');
  }
  const orderIds = currentOrderIds();
  return sortNewest(store.map((record) => buildView(record, orderIds)));
}

/**
 * Flag or unflag a notification as handled. Returns the updated view (or null
 * when the record is unknown) so the caller can update state without a refetch.
 * No live emit here — this is not a new arrival.
 */
export function setNotificationProcessed(
  id: string,
  processed: boolean,
): NotificationView | null {
  if (processed) {
    processedIds.add(id);
  } else {
    processedIds.delete(id);
  }
  const record = store.find((item) => item.id === id);
  if (!record) {
    return null;
  }
  return buildView(record, currentOrderIds());
}

/**
 * Flag every notification in the log as handled at once. Returns the updated
 * views so the caller can update state without a refetch. No live emit here —
 * this is a bulk acknowledgement, not a new arrival.
 */
export function setAllNotificationsProcessed(): NotificationView[] {
  store.forEach((record) => processedIds.add(record.id));
  const orderIds = currentOrderIds();
  return sortNewest(store.map((record) => buildView(record, orderIds)));
}

/** Add an incoming notification to the log and notify live listeners. */
export function pushNotification(record: NotificationRecord): NotificationView {
  store = [{ ...record }, ...store];
  const view = buildView(record, currentOrderIds());
  emit(view);
  return view;
}

/**
 * Preview-only: push one clearly-labelled sample event so the live toast + log
 * append can be exercised. Never runs on a timer and is never presented as a
 * real system event.
 */
let liveSequence = 0;
export function simulateIncomingEvent(): NotificationView {
  liveSequence += 1;
  return pushNotification({
    id: `NTF-LIVE-${liveSequence}`,
    kind: 'new_order',
    createdAt: new Date().toISOString(),
    orderId: 'LB-1041',
  });
}